import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import styled from 'styled-components'
import toast, { Toaster } from 'react-hot-toast'
import Joi from 'joi'

import Modal from '../../../../../../components/Modal'
import TechnicalSkillInput from '../../../../../../components/TechnicalSkillsInput'
import { MultiSelect } from '../../../../../../components/Select'

import apiClient from '../../../../../../helpers/apiClient'

import { setHasSkillChange } from '../../../../../../redux/feature/jobSlice'

import { getFromLocalStorage } from '../../../../../../utils/util'

import {
  setProfessionalHeadlines as updateProfessionalHeadlines,
  setTechnicalSkills as updateTechnicalSkills,
  setSoftSkills as updateSoftSkills,
} from '../../../../../../redux/feature/applicantSlice'

const SkillsUpdate = () => {
  const dispatch = useDispatch()

  const [technicalSkills, setTechnicalSkills] = useState([])
  const [professionalHeadlineIds, setProfessionalHeadlinesIds] = useState([])
  const [softSkills, setSoftSkills] = useState([])
  const [professionalHeadlines, setProfessionalHeadlines] = useState([])

  const [professionalHeadlineList, setProfessionalHeadlineList] = useState([])
  const [softSkillList, setSoftSkillList] = useState([])

  const [fieldErrors, setFieldErrors] = useState({})

  const cvData = getFromLocalStorage('applicant-cv-data')
  const meta = cvData.meta !== null ? JSON.parse(cvData.meta) : null
  const currentProfessionalHeadlines = JSON.parse(cvData?.professional_headline)
  const userId = useMemo(() => getFromLocalStorage('user_id'), [])

  const validationSchema = useMemo(() => {
    return Joi.object({
      professionalHeadlines: Joi.array().label('Professional Headlines').min(1),
      technicalSkills: Joi.array().label('Technical Skills').min(1),
      softSkills: Joi.array().label('Soft Skills').min(5),
    })
  }, [])

  const getProfessionalHeadlines = () => {
    apiClient
      .get(`/professional-headlines?withSkills=true`)
      .then((res) => {
        const result = res?.data?.resp
        const newProfessionalHeadlines = result?.map((e) => ({
          value: e.id,
          label: e.professional_headline,
        }))

        setProfessionalHeadlineList(newProfessionalHeadlines)
      })
      .catch((err) => console.log(err))
  }

  const getSoftSkills = () => {
    apiClient
      .get(`/skills`)
      .then((res) => {
        const result = res?.data?.data?.softSkills
        const newSoftSkills = result?.map((e) => ({ value: e, label: e }))

        setSoftSkillList(newSoftSkills)
      })
      .catch((err) => console.log(err))
  }

  const handleTechnicalSkills = (existingSkills) => {
    setTechnicalSkills(
      existingSkills?.map((e) => ({
        ...e,
        skill: e.label,
        id: e.value,
        experience: e?.experience || 1,
      }))
    )
  }

  const handleProfessionalHeadlines = (existingHeadlines) =>
    setProfessionalHeadlines(existingHeadlines)
  const handleSoftSkills = (existingSkills) => setSoftSkills(existingSkills)

  const handleUpdate = () => {
    const data = {
      professionalHeadlines,
      technicalSkills,
      softSkills,
    }

    const result = validationSchema.validate(data, { abortEarly: false })

    if (result?.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      return toast.error(
        'Please correct the validation errors before proceeding.'
      )
    }

    const newMeta = {
      ...meta,
      fullSkills: [technicalSkills, softSkills],
    }

    const payload = {
      meta: JSON.stringify(newMeta),
      professional_headline: JSON.stringify(professionalHeadlines),
    }

    apiClient
      .put(`/applicant/${userId}/update`, payload)
      .then((resp) => {
        localStorage.setItem(
          'applicant-cv-data',
          JSON.stringify({
            ...cvData,
            meta: resp?.data?.data?.meta,
            professional_headline: resp?.data?.data?.professional_headline,
          })
        )

        dispatch(updateProfessionalHeadlines(professionalHeadlines))
        dispatch(updateTechnicalSkills(technicalSkills))
        dispatch(updateSoftSkills(softSkills))

        toast.success(resp?.data?.message)

        dispatch(setHasSkillChange(true))
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message)
      })

    setFieldErrors({})
  }

  useEffect(() => {
    // fetch requests from server
    getProfessionalHeadlines()
    getSoftSkills()

    // load data from localStorage
    const formattedProfessionalHeadlines = currentProfessionalHeadlines?.map(
      (e) => {
        // Handle both possible object formats
        const value = e?.value ?? e?.id
        const label = e?.label ?? e?.item

        return { value, label }
      }
    )
    setProfessionalHeadlines(formattedProfessionalHeadlines)

    const currentTechnicalSkills = meta?.fullSkills[0]
    setTechnicalSkills(currentTechnicalSkills)

    const currentSoftSkills = meta?.fullSkills[1]
    setSoftSkills(currentSoftSkills)
  }, [])

  useEffect(() => {
    const formattedProfessionalHeadlines = currentProfessionalHeadlines?.map(
      (e) => {
        // Handle both possible object formats
        const value = e?.value ?? e?.id
        const label = e?.label ?? e?.item

        return { value, label }
      }
    )

    const headlineIds = formattedProfessionalHeadlines?.map((e) => e.value)
    setProfessionalHeadlinesIds(headlineIds)
  }, [professionalHeadlines])

  return (
    <Modal title="Skills" btnTitle="Update" btnFunc={handleUpdate}>
      <StyledSkillUpdate>
        <MultiSelect
          title="Professional Headlines"
          options={professionalHeadlineList}
          selected={professionalHeadlines}
          placeholder="Select Professional Headlines"
          onChange={handleProfessionalHeadlines}
          marginBottom="0"
          wrapperClass={fieldErrors?.professionalHeadlines && 'error'}
          subText={fieldErrors?.professionalHeadlines}
        />

        <TechnicalSkillInput
          skills={technicalSkills}
          onSelectTechnicalSkills={handleTechnicalSkills}
          professionalHeadlineIds={professionalHeadlineIds}
          fieldError={fieldErrors?.technicalSkills}
        />

        <MultiSelect
          title="Soft Skills"
          options={softSkillList}
          selected={softSkills}
          placeholder="Select Soft Skills"
          onChange={handleSoftSkills}
          wrapperClass={fieldErrors?.softSkills && 'error'}
          subText={fieldErrors?.softSkills}
        />
      </StyledSkillUpdate>

      <Toaster position="top-right" reverseOrder={false} />
    </Modal>
  )
}

const StyledSkillUpdate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-height: 90vh;
  padding-right: 0.5rem;
`

export default SkillsUpdate
